import React, { useEffect, useMemo, useState } from 'react';
import { US_STATES } from '../../lib/usStates';
import {
  ArrowLeft, AlertCircle, CheckCircle, FileText, Loader2, Save,
  Package, Truck, User, Stethoscope, XCircle, Copy, Search
} from 'lucide-react';
import { apiClient } from '../../lib/api';
import { PrescriptionTransfer, TransferStatusBadge } from '../../pages/PrescriptionTransfers';
import { AsyncSearchSelect } from '../common/AsyncSearchSelect';

/**
 * Review one transferred prescription and turn it into an order.
 *
 * Left: what we read off the script, every field editable — the parse is a
 * starting point, not an authority, and the pharmacist owns the final content.
 * Right: the order being built from it — catalog product (preselected when the
 * match is unambiguous), sig, quantity, and where it ships.
 */

interface MatchCandidate {
  productId: string;
  displayName: string;
  drugName: string;
  concentration: string;
  confidence: number;
  reason: string;
}

const field = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500';
const label = 'block text-xs font-medium text-gray-700 mb-1';

function toDateInput(value?: string): string {
  if (!value) return '';
  const date = new Date(value);
  return isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
}

export const PrescriptionTransferReview: React.FC<{
  transferId: string;
  onClose: () => void;
}> = ({ transferId, onClose }) => {
  const [transfer, setTransfer] = useState<PrescriptionTransfer | null>(null);
  const [candidates, setCandidates] = useState<MatchCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Editable copies of the parsed sections.
  const [prescriber, setPrescriber] = useState<any>({});
  const [patient, setPatient] = useState<any>({});
  const [medication, setMedication] = useState<any>({});
  const [notes, setNotes] = useState('');

  // Order builder.
  const [productId, setProductId] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [sig, setSig] = useState('');
  const [refills, setRefills] = useState(0);
  const [shipTo, setShipTo] = useState<'patient' | 'prescriber' | 'clinic'>('patient');
  const [patientId, setPatientId] = useState('');
  // The chosen patient record, for the picker's label. patientId stays the
  // source of truth (it is what the transfer saves).
  const [patientRecord, setPatientRecord] = useState<any | null>(null);

  // Catalog search. The matcher only surfaces products whose name resembles the
  // drug text on the script, so a script written under a brand name, an
  // abbreviation, or a compounded description it cannot score leaves the
  // suggestion list empty — even though the pharmacy is entitled to order the
  // product. Search is therefore always available, not just a fallback for when
  // there are no suggestions.
  const [productQuery, setProductQuery] = useState('');
  const [productResults, setProductResults] = useState<any[]>([]);
  const [searchingCatalog, setSearchingCatalog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<{ productId: string; displayName: string } | null>(null);

  const load = async () => {
    setLoading(true);
    const res = await apiClient.getPrescriptionTransfer(transferId);
    if (!res.success || !res.data) {
      setError(res.error || 'Could not load this transfer.');
      setLoading(false);
      return;
    }
    const t = res.data.transfer as PrescriptionTransfer;
    setTransfer(t);
    setCandidates(res.data.candidates || []);
    setPrescriber({ ...(t.parsed?.prescriber || {}) });
    setPatient({ ...(t.parsed?.patient || {}) });
    setMedication({ ...(t.parsed?.medication || {}) });
    setNotes(t.notes || '');
    setProductId(t.matchedProductId || '');
    setSig(t.parsed?.medication?.sig || '');
    setRefills(t.parsed?.medication?.refills ?? 0);
    setPatientId(t.patientId || '');
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [transferId]);

  // Resolve a saved patientId to its record so the picker can label it. The
  // picker itself searches the whole roster on the server; the old <select>
  // held only the 200 newest patients.
  useEffect(() => {
    if (!patientId) { setPatientRecord(null); return; }
    if (patientRecord && String(patientRecord._id) === String(patientId)) return;
    let cancelled = false;
    (async () => {
      const res = await apiClient.getPatient(patientId);
      if (!cancelled && res.success && res.data) setPatientRecord((res.data as any).patient || res.data);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId]);

  // Debounced catalog search. /products restricts a non-admin account to the
  // products in its own assigned catalogs, so this cannot offer something the
  // pharmacy is not entitled to order.
  useEffect(() => {
    const query = productQuery.trim();
    if (query.length < 2) {
      setProductResults([]);
      setSearchingCatalog(false);
      return;
    }
    let cancelled = false;
    setSearchingCatalog(true);
    const timer = setTimeout(async () => {
      const res = await apiClient.getProducts({ search: query, limit: 20 });
      if (cancelled) return;
      setProductResults(res.success ? ((res.data as any)?.products || []) : []);
      setSearchingCatalog(false);
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [productQuery]);

  // Resolve what is actually selected, so the pharmacist can always see it by
  // name. A product chosen by search is not in `candidates`, and one chosen in
  // an earlier session may be in neither list — that one is fetched by id
  // rather than left showing nothing.
  useEffect(() => {
    if (!productId) { setSelectedProduct(null); return; }
    const known =
      candidates.find(c => c.productId === productId) ||
      productResults.find((p: any) => String(p._id) === productId);
    if (known) {
      setSelectedProduct({
        productId,
        displayName: (known as any).displayName || (known as any).drugName || 'Selected product'
      });
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await apiClient.getProductsByIds([productId]);
      if (cancelled) return;
      const product = res.success ? (res.data as any)?.products?.[0] : null;
      setSelectedProduct({
        productId,
        displayName: product?.displayName || product?.drugName || 'Selected product'
      });
    })();
    return () => { cancelled = true; };
  }, [productId, candidates, productResults]);

  const warnings = transfer?.raw?.parseWarnings || [];
  const isOrdered = transfer?.status === 'ordered';
  const isRejected = transfer?.status === 'rejected';
  const readOnly = isOrdered || isRejected;

  const autoMatched = useMemo(
    () => candidates.find(c => c.productId === productId && (transfer?.matchConfidence ?? 0) > 0 && transfer?.matchReason !== 'Selected by pharmacist'),
    [candidates, productId, transfer]
  );

  const buildPatch = () => ({
    parsed: { prescriber, patient, medication: { ...medication, sig, refills } },
    notes,
    matchedProductId: productId || null,
    patientId: patientId || null
  });

  const handleSave = async (markReady = false) => {
    setSaving(true);
    setError('');
    setMessage('');
    const res = await apiClient.updatePrescriptionTransfer(transferId, {
      ...buildPatch(),
      ...(markReady ? { status: 'ready' as const } : {})
    });
    setSaving(false);
    if (!res.success) {
      setError(res.error || 'Could not save your changes.');
      return;
    }
    setMessage('Saved.');
    await load();
  };

  const openDocument = async () => {
    const res = await apiClient.getPrescriptionTransferDocument(transferId);
    if (res.success && res.data?.downloadUrl) {
      window.open(res.data.downloadUrl, '_blank', 'noopener');
    } else {
      setError(res.error || 'Could not open the document.');
    }
  };

  const handleReject = async () => {
    const reason = window.prompt('Why are you rejecting this transfer?');
    if (reason === null) return;
    const res = await apiClient.rejectPrescriptionTransfer(transferId, reason);
    if (!res.success) {
      setError(res.error || 'Could not reject this transfer.');
      return;
    }
    onClose();
  };

  const handleCreatePatient = async () => {
    setError('');
    if (!patient.firstName || !patient.lastName || !patient.dateOfBirth) {
      setError('A first name, last name and date of birth are needed to create the patient.');
      return;
    }
    const res = await apiClient.createPatient({
      firstName: patient.firstName,
      lastName: patient.lastName,
      dateOfBirth: patient.dateOfBirth,
      phone: patient.phone || '',
      ...(patient.email ? { email: patient.email } : {}),
      address: {
        street: patient.address?.street || '',
        ...(patient.address?.street2 ? { street2: patient.address.street2 } : {}),
        city: patient.address?.city || '',
        state: patient.address?.state || '',
        zipCode: patient.address?.zipCode || ''
      }
    });
    if (!res.success) {
      setError(res.error || 'Could not create the patient.');
      return;
    }
    const createdPatient = (res.data as any)?.patient || res.data;
    const created = createdPatient?._id;
    if (!created) {
      setError('The patient was created but came back without an id — reload before ordering.');
      return;
    }
    // Select the record we already have, so the picker labels it at once.
    setPatientRecord(createdPatient);
    setPatientId(created);
    setMessage('Patient created from the script and selected.');
  };

  const handlePlaceOrder = async () => {
    setError('');
    if (!patientId) return setError('Choose which patient this script is for, or create them from it.');
    if (!productId) return setError('Choose which catalog product fills this script.');
    if (!sig.trim()) return setError('The directions (sig) cannot be empty.');

    setPlacing(true);
    // Persist the corrections first, so the order freezes the reviewed content.
    const saved = await apiClient.updatePrescriptionTransfer(transferId, buildPatch());
    if (!saved.success) {
      setPlacing(false);
      return setError(saved.error || 'Could not save your changes before ordering.');
    }

    const res = await apiClient.createOrder({
      patientId,
      paymentMethod: 'physician_billed',
      shippingDestination: shipTo,
      prescriptionTransferId: transferId,
      items: [{
        productId,
        quantity,
        selectedVariants: {
          prescriptionData: JSON.stringify({
            sig,
            refills,
            daysSupply: medication.daysSupply || 30,
            pharmacyNotes: notes || undefined
          })
        }
      }]
    });
    setPlacing(false);

    if (!res.success) {
      return setError(res.error || 'Could not place the order.');
    }
    const orderId = res.data?._id || res.data?.orderId;
    const written = await apiClient.writeScript(orderId);
    if (!written.success) {
      return setError(written.error || 'The order was created but the script could not be written.');
    }
    onClose();
  };

  if (loading) {
    return (
      <div className="p-12 text-center text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Loading…
      </div>
    );
  }

  if (!transfer) {
    return (
      <div className="p-6">
        <button onClick={onClose} className="flex items-center gap-1 text-blue-600 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <p className="text-red-600">{error || 'Transfer not found.'}</p>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <button onClick={onClose} className="flex items-center gap-1 text-blue-600 mb-4 hover:text-blue-700">
        <ArrowLeft className="w-4 h-4" /> Back to transfers
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Review transferred prescription</h1>
          <div className="flex items-center gap-2 mt-2">
            <TransferStatusBadge status={transfer.status} />
            {transfer.parsed?.transmission?.messageId && (
              <span className="text-xs text-gray-500">Message {transfer.parsed.transmission.messageId}</span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {transfer.documentS3Key && (
            <button onClick={openDocument} className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
              <FileText className="w-4 h-4" /> View original
            </button>
          )}
          {!readOnly && (
            <button onClick={handleReject} className="flex items-center gap-2 px-3 py-2 border border-red-200 text-red-700 rounded-lg text-sm hover:bg-red-50">
              <XCircle className="w-4 h-4" /> Reject
            </button>
          )}
        </div>
      </div>

      {transfer.duplicateOfId && (
        <div className="mb-4 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
          <Copy className="w-4 h-4 shrink-0 mt-0.5" />
          An earlier transfer carries the same transmission id — this may be a resend of a script you already have.
        </div>
      )}

      {warnings.length > 0 && !readOnly && (
        <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <div className="flex items-center gap-2 text-sm font-medium text-amber-900 mb-1">
            <AlertCircle className="w-4 h-4" /> Please confirm before ordering
          </div>
          <ul className="list-disc list-inside text-sm text-amber-900 space-y-0.5">
            {warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {isOrdered && (
        <div className="mb-4 flex items-center gap-2 bg-indigo-50 border border-indigo-200 rounded-lg p-3 text-sm text-indigo-900">
          <CheckCircle className="w-4 h-4" /> This transfer has been ordered and is no longer editable.
        </div>
      )}
      {isRejected && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
          <XCircle className="w-4 h-4" /> Rejected{transfer.rejectedReason ? `: ${transfer.rejectedReason}` : ''}.
        </div>
      )}
      {error && (
        <div className="mb-4 flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0" /> {error}
        </div>
      )}
      {message && (
        <div className="mb-4 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg p-3 text-sm text-green-800">
          <CheckCircle className="w-4 h-4 shrink-0" /> {message}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── What the script says ─────────────────────────────────────── */}
        <div className="space-y-5">
          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
              <Stethoscope className="w-4 h-4 text-gray-500" /> Prescribing doctor
            </h2>
            <p className="text-xs text-gray-500 mb-3">
              This doctor prescribes on their own credential — their NPI is what we send to the
              fulfilling pharmacy, so it has to be right.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>First name *</label>
                <input className={field} disabled={readOnly} value={prescriber.firstName || ''}
                  onChange={e => setPrescriber({ ...prescriber, firstName: e.target.value })} />
              </div>
              <div>
                <label className={label}>Last name *</label>
                <input className={field} disabled={readOnly} value={prescriber.lastName || ''}
                  onChange={e => setPrescriber({ ...prescriber, lastName: e.target.value })} />
              </div>
              <div>
                <label className={label}>NPI *</label>
                <input className={field} disabled={readOnly} inputMode="numeric" maxLength={10}
                  value={prescriber.npiNumber || ''}
                  onChange={e => setPrescriber({ ...prescriber, npiNumber: e.target.value.replace(/\D/g, '') })} />
              </div>
              <div>
                <label className={label}>License state</label>
                <select className={field} disabled={readOnly} value={prescriber.licenseState || ''}
                  onChange={e => setPrescriber({ ...prescriber, licenseState: e.target.value })}>
                  <option value="">—</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="col-span-2">
                <label className={label}>Office address (used when shipping to the doctor)</label>
                <input className={field} disabled={readOnly} placeholder="Street"
                  value={prescriber.address?.street || ''}
                  onChange={e => setPrescriber({ ...prescriber, address: { ...prescriber.address, street: e.target.value } })} />
              </div>
              <div>
                <input className={field} disabled={readOnly} placeholder="City"
                  value={prescriber.address?.city || ''}
                  onChange={e => setPrescriber({ ...prescriber, address: { ...prescriber.address, city: e.target.value } })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select className={field} disabled={readOnly} value={prescriber.address?.state || ''}
                  onChange={e => setPrescriber({ ...prescriber, address: { ...prescriber.address, state: e.target.value } })}>
                  <option value="">State</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input className={field} disabled={readOnly} placeholder="ZIP"
                  value={prescriber.address?.zipCode || ''}
                  onChange={e => setPrescriber({ ...prescriber, address: { ...prescriber.address, zipCode: e.target.value } })} />
              </div>
            </div>
          </section>

          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
              <User className="w-4 h-4 text-gray-500" /> Patient on the script
            </h2>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={label}>First name</label>
                <input className={field} disabled={readOnly} value={patient.firstName || ''}
                  onChange={e => setPatient({ ...patient, firstName: e.target.value })} />
              </div>
              <div>
                <label className={label}>Last name</label>
                <input className={field} disabled={readOnly} value={patient.lastName || ''}
                  onChange={e => setPatient({ ...patient, lastName: e.target.value })} />
              </div>
              <div>
                <label className={label}>Date of birth</label>
                <input className={field} disabled={readOnly} type="date" value={toDateInput(patient.dateOfBirth)}
                  onChange={e => setPatient({ ...patient, dateOfBirth: e.target.value })} />
              </div>
              <div>
                <label className={label}>Phone</label>
                <input className={field} disabled={readOnly} value={patient.phone || ''}
                  onChange={e => setPatient({ ...patient, phone: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className={label}>Email</label>
                <input className={field} disabled={readOnly} type="email" value={patient.email || ''}
                  onChange={e => setPatient({ ...patient, email: e.target.value })} />
              </div>
              {/* Read off the script and used both to create the patient record and
                  as the shipping destination, so it has to be visible and correctable
                  here — not applied out of sight. */}
              <div className="col-span-2">
                <label className={label}>Home address (used when shipping to the patient)</label>
                <input className={field} disabled={readOnly} placeholder="Street"
                  value={patient.address?.street || ''}
                  onChange={e => setPatient({ ...patient, address: { ...patient.address, street: e.target.value } })} />
              </div>
              <div className="col-span-2">
                <input className={field} disabled={readOnly} placeholder="Apt, suite, unit (optional)"
                  value={patient.address?.street2 || ''}
                  onChange={e => setPatient({ ...patient, address: { ...patient.address, street2: e.target.value } })} />
              </div>
              <div>
                <input className={field} disabled={readOnly} placeholder="City"
                  value={patient.address?.city || ''}
                  onChange={e => setPatient({ ...patient, address: { ...patient.address, city: e.target.value } })} />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <select className={field} disabled={readOnly} value={patient.address?.state || ''}
                  onChange={e => setPatient({ ...patient, address: { ...patient.address, state: e.target.value } })}>
                  <option value="">State</option>
                  {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                <input className={field} disabled={readOnly} placeholder="ZIP"
                  value={patient.address?.zipCode || ''}
                  onChange={e => setPatient({ ...patient, address: { ...patient.address, zipCode: e.target.value } })} />
              </div>
            </div>
          </section>

          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
              <FileText className="w-4 h-4 text-gray-500" /> As written
            </h2>
            <div className="space-y-3">
              <div>
                <label className={label}>Drug as written</label>
                <input className={field} disabled={readOnly} value={medication.description || ''}
                  onChange={e => setMedication({ ...medication, description: e.target.value })} />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className={label}>Written date</label>
                  <input className={field} disabled={readOnly} type="date" value={toDateInput(medication.writtenDate)}
                    onChange={e => setMedication({ ...medication, writtenDate: e.target.value })} />
                </div>
                <div>
                  <label className={label}>Qty on script</label>
                  <input className={field} disabled value={`${medication.quantity ?? '—'} ${medication.quantityUnit || ''}`.trim()} />
                </div>
                <div>
                  <label className={label}>Diagnosis</label>
                  <input className={field} disabled value={medication.diagnosisCode || '—'} />
                </div>
              </div>
              {transfer.parsed?.observations && transfer.parsed.observations.length > 0 && (
                <div className="text-xs text-gray-600 bg-gray-50 rounded p-2">
                  {transfer.parsed.observations.map((o, i) => (
                    <span key={i} className="mr-3">{o.name}: {o.value} {o.unit}</span>
                  ))}
                </div>
              )}
              <div>
                <label className={label}>Notes</label>
                <textarea className={field} rows={2} disabled={readOnly} value={notes}
                  placeholder="Anything the fulfilling pharmacy should know."
                  onChange={e => setNotes(e.target.value)} />
              </div>
            </div>
          </section>
        </div>

        {/* ── The order being built from it ────────────────────────────── */}
        <div className="space-y-5">
          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
              <Package className="w-4 h-4 text-gray-500" /> What we'll dispense
            </h2>

            {autoMatched && (
              <div className="mb-3 flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-2.5 text-xs text-green-900">
                <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>Matched automatically — {autoMatched.reason}. Change it if that's not right.</span>
              </div>
            )}

            <label className={label}>Catalog product *</label>

            {selectedProduct && (
              <div className="flex items-center justify-between gap-2 bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-sm">
                <span className="text-blue-900 min-w-0">
                  <span className="font-medium">Dispensing:</span>{' '}
                  <span className="break-words">{selectedProduct.displayName}</span>
                </span>
                {!readOnly && (
                  <button type="button" onClick={() => { setProductId(''); setProductQuery(''); }}
                    className="shrink-0 text-xs text-blue-700 hover:text-blue-900 underline">
                    Change
                  </button>
                )}
              </div>
            )}

            {!selectedProduct && candidates.length > 0 && (
              <select className={field} disabled={readOnly} value="" onChange={e => setProductId(e.target.value)}>
                <option value="">Choose a suggested product…</option>
                {candidates.map(c => (
                  <option key={c.productId} value={c.productId}>
                    {c.displayName} — {Math.round(c.confidence * 100)}% match
                  </option>
                ))}
              </select>
            )}

            {!selectedProduct && !readOnly && (
              <div className={candidates.length > 0 ? 'mt-2' : ''}>
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <input
                    className={`${field} pl-9`}
                    value={productQuery}
                    onChange={e => setProductQuery(e.target.value)}
                    placeholder={candidates.length > 0
                      ? 'Or search the full catalog…'
                      : 'Search the catalog by drug or product name…'}
                  />
                  {searchingCatalog && (
                    <Loader2 className="w-4 h-4 text-gray-400 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />
                  )}
                </div>

                {productQuery.trim().length >= 2 && !searchingCatalog && (
                  productResults.length > 0 ? (
                    <ul className="mt-1 max-h-56 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
                      {productResults.map((p: any) => (
                        <li key={p._id}>
                          <button type="button" onClick={() => setProductId(String(p._id))}
                            className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50">
                            <span className="text-gray-900">{p.displayName || p.drugName}</span>
                            {p.concentration && <span className="text-gray-500"> — {p.concentration}</span>}
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-1 text-xs text-gray-500 px-1">
                      Nothing in this pharmacy's catalog matches “{productQuery.trim()}”.
                    </p>
                  )
                )}

                {candidates.length === 0 && productQuery.trim().length < 2 && (
                  <p className="mt-2 text-xs text-gray-500">
                    Nothing in the catalog resembles the drug text on the left, so search for it by
                    name. If the pharmacy genuinely cannot dispense it, order it through the normal
                    ordering flow instead.
                  </p>
                )}
              </div>
            )}

            <div className="grid grid-cols-2 gap-3 mt-3">
              <div>
                <label className={label}>Quantity *</label>
                <input className={field} disabled={readOnly} type="number" min={1} value={quantity}
                  onChange={e => setQuantity(Math.max(1, Number(e.target.value)))} />
              </div>
              <div>
                <label className={label}>Refills</label>
                <input className={field} disabled={readOnly} type="number" min={0} max={5} value={refills}
                  onChange={e => setRefills(Number(e.target.value))} />
              </div>
            </div>

            <div className="mt-3">
              <label className={label}>Directions (sig) *</label>
              <textarea className={field} rows={3} disabled={readOnly} value={sig}
                onChange={e => setSig(e.target.value)} />
              <p className={`text-xs mt-1 ${sig.length > 150 ? 'text-red-600' : 'text-gray-500'}`}>
                {sig.length}/150 — the fulfilling pharmacy rejects anything longer.
              </p>
            </div>
          </section>

          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
              <User className="w-4 h-4 text-gray-500" /> Who it's for
            </h2>
            <AsyncSearchSelect<any>
              fetchPage={({ page, pageSize, search, signal }) =>
                apiClient.searchPatientsPage({ page, pageSize, search, signal, sortBy: 'name', sortOrder: 'asc' })}
              getKey={(p) => String(p._id)}
              getLabel={(p) => `${p.firstName} ${p.lastName}`}
              getDescription={(p) => p.dateOfBirth ? new Date(p.dateOfBirth).toLocaleDateString() : (p.email || p.phone)}
              value={patientRecord}
              onChange={(p) => { setPatientRecord(p); setPatientId(p ? String(p._id) : ''); }}
              placeholder="Search patients by name, phone or email…"
              noun="patients"
              disabled={readOnly}
              aria-label="Choose a patient"
            />
            {!patientId && !readOnly && (
              <button onClick={handleCreatePatient} className="mt-2 text-sm text-blue-600 hover:text-blue-700">
                Create this patient from the script
              </button>
            )}
          </section>

          <section className="bg-white border border-gray-200 rounded-lg p-4">
            <h2 className="flex items-center gap-2 font-semibold text-gray-900 mb-3">
              <Truck className="w-4 h-4 text-gray-500" /> Where it ships
            </h2>
            <div className="space-y-2">
              {([
                { value: 'patient', title: 'To the patient', hint: 'Direct to the address on their record.' },
                { value: 'prescriber', title: "To the prescribing doctor", hint: "The office address on the script — fill that in on the left if it's blank." },
                { value: 'clinic', title: 'To your pharmacy', hint: 'Back to you to dispense.' }
              ] as const).map(option => (
                <label key={option.value}
                  className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${shipTo === option.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`}>
                  <input type="radio" name="shipTo" className="mt-1" disabled={readOnly}
                    checked={shipTo === option.value} onChange={() => setShipTo(option.value)} />
                  <span>
                    <span className="block text-sm font-medium text-gray-900">{option.title}</span>
                    <span className="block text-xs text-gray-500">{option.hint}</span>
                  </span>
                </label>
              ))}
            </div>
          </section>

          {!readOnly && (
            <div className="flex items-center gap-2">
              <button onClick={() => handleSave(false)} disabled={saving}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save changes
              </button>
              <button onClick={handlePlaceOrder} disabled={placing || saving}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50">
                {placing ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
                {placing ? 'Placing order…' : 'Place order'}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
